/**
 * MyFlexibleWorkingRequests.jsx
 * 
 * Employee view of their flexible working requests.
 * Shows status, deadlines, decisions, and allows appeals.
 */

import React, { useState, useEffect } from 'react';
import api from './axiosConfig';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  Divider,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Collapse,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Schedule,
  ExpandMore,
  ExpandLess,
  Gavel,
  AccessTime,
  Event,
  Description,
  HourglassEmpty,
  ThumbUp,
  ThumbDown
} from '@mui/icons-material';

// ============================================================
// CONSTANTS
// ============================================================

const FLEXIBILITY_TYPE_NAMES = {
  part_time: 'Part-Time Working',
  compressed: 'Compressed Hours',
  flexitime: 'Flexitime',
  remote: 'Remote Working',
  hybrid: 'Hybrid Working',
  job_share: 'Job Share',
  term_time: 'Term-Time Only',
  other: 'Other Arrangement'
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'default', icon: Description },
  submitted: { label: 'Submitted', color: 'primary', icon: Schedule },
  acknowledged: { label: 'Acknowledged', color: 'info', icon: CheckCircle },
  consultation_scheduled: { label: 'Consultation Scheduled', color: 'warning', icon: Event },
  consultation_held: { label: 'Consultation Held', color: 'secondary', icon: CheckCircle },
  approved: { label: 'Approved', color: 'success', icon: ThumbUp },
  approved_trial: { label: 'Approved (Trial)', color: 'success', icon: HourglassEmpty },
  refused: { label: 'Refused', color: 'error', icon: ThumbDown },
  appealed: { label: 'Appealed', color: 'warning', icon: Gavel },
  appeal_upheld: { label: 'Appeal Upheld', color: 'success', icon: ThumbUp },
  appeal_rejected: { label: 'Appeal Rejected', color: 'error', icon: ThumbDown },
  withdrawn: { label: 'Withdrawn', color: 'default', icon: Cancel }
};

// Process steps for timeline
const PROCESS_STEPS = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'acknowledged', label: 'Acknowledged' },
  { key: 'consultation', label: 'Consultation' },
  { key: 'decision', label: 'Decision' }
];


// ============================================================
// MAIN COMPONENT
// ============================================================

const MyFlexibleWorkingRequests = ({ onNewRequest }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [appealDialog, setAppealDialog] = useState({ open: false, request: null });
  const [withdrawDialog, setWithdrawDialog] = useState({ open: false, request: null });

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/flexible-working/requests');
      setRequests(response.data.requests || []);
    } catch (err) {
      setError('Failed to load your requests');
    } finally {
      setLoading(false);
    }
  };

  // Filter by tab
  const filteredRequests = requests.filter(r => {
    if (tabValue === 0) return true;
    if (tabValue === 1) return ['submitted', 'acknowledged', 'consultation_scheduled', 'consultation_held'].includes(r.status);
    if (tabValue === 2) return ['approved', 'approved_trial'].includes(r.status);
    if (tabValue === 3) return ['refused', 'appealed', 'appeal_upheld', 'appeal_rejected', 'withdrawn'].includes(r.status);
    return true;
  });

  // Calculate active step for timeline
  const getActiveStep = (req) => {
    if (['approved', 'approved_trial', 'refused', 'appealed', 'appeal_upheld', 'appeal_rejected'].includes(req.status)) return 4;
    if (['consultation_scheduled', 'consultation_held'].includes(req.status)) return 2;
    if (req.status === 'acknowledged') return 1;
    if (req.status === 'submitted') return 0;
    return 0;
  };

  // Handle appeal submission
  const handleAppeal = async (grounds) => {
    try {
      await api.post(`/flexible-working/requests/${appealDialog.request.id}/appeal`, {
        appeal_grounds: grounds
      });
      setAppealDialog({ open: false, request: null });
      loadRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit appeal');
    }
  };

  // Handle withdrawal
  const handleWithdraw = async () => {
    try {
      await api.post(`/flexible-working/requests/${withdrawDialog.request.id}/withdraw`);
      setWithdrawDialog({ open: false, request: null });
      loadRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to withdraw request');
    }
  };

  // ============================================================
  // RENDER REQUEST CARD
  // ============================================================

  const renderRequestCard = (req) => {
    const isExpanded = expandedId === req.id;
    const statusConfig = STATUS_CONFIG[req.status] || {};
    const StatusIcon = statusConfig.icon || Schedule;
    const activeStep = getActiveStep(req);

    return (
      <Card key={req.id} variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h6">
                {FLEXIBILITY_TYPE_NAMES[req.flexibility_type] || req.flexibility_type}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Submitted {req.submitted_at ? new Date(req.submitted_at).toLocaleDateString('en-GB') : '-'}
              </Typography>
            </Box>
            <Chip 
              icon={<StatusIcon fontSize="small" />}
              label={statusConfig.label} 
              color={statusConfig.color} 
              size="small" 
            />
          </Box>

          {/* Progress Timeline (for pending requests) */}
          {['submitted', 'acknowledged', 'consultation_scheduled', 'consultation_held'].includes(req.status) && (
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
              {PROCESS_STEPS.map((step) => (
                <Step key={step.key}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {/* Key Info */}
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Proposed Start</Typography>
              <Typography variant="body2">
                {req.proposed_start_date ? new Date(req.proposed_start_date).toLocaleDateString('en-GB') : '-'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Response Deadline</Typography>
              <Typography variant="body2" color={req.days_until_deadline < 7 ? 'warning.main' : 'inherit'}>
                {req.response_deadline ? new Date(req.response_deadline).toLocaleDateString('en-GB') : '-'}
              </Typography>
            </Grid>
            {req.effective_date && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Effective Date</Typography>
                <Typography variant="body2" color="success.main">
                  {new Date(req.effective_date).toLocaleDateString('en-GB')}
                </Typography>
              </Grid>
            )}
            {req.is_trial && req.trial_end_date && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Trial Ends</Typography>
                <Typography variant="body2">
                  {new Date(req.trial_end_date).toLocaleDateString('en-GB')}
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* Approved Alert */}
          {req.status === 'approved' && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <AlertTitle>Request Approved</AlertTitle>
              Your flexible working request has been approved. 
              {req.effective_date && ` Effective from ${new Date(req.effective_date).toLocaleDateString('en-GB')}.`}
            </Alert>
          )}

          {/* Trial Alert */}
          {req.status === 'approved_trial' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>Approved on Trial Basis</AlertTitle>
              Your request has been approved for a trial period
              {req.trial_end_date && ` ending ${new Date(req.trial_end_date).toLocaleDateString('en-GB')}`}.
            </Alert>
          )}

          {/* Refused Alert */}
          {req.status === 'refused' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle>Request Refused</AlertTitle>
              {req.decision_explanation && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Reason:</strong> {req.decision_explanation}
                </Typography>
              )}
              {req.alternative_offered && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Alternative offered:</strong> {req.alternative_offered}
                </Typography>
              )}
            </Alert>
          )}

          {/* Expandable Details */}
          <Collapse in={isExpanded}>
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Current Pattern</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', backgroundColor: 'grey.50', p: 1, borderRadius: 1 }}>
                  {req.current_pattern}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Requested Pattern</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', backgroundColor: 'primary.lighter', p: 1, borderRadius: 1 }}>
                  {req.requested_pattern}
                </Typography>
              </Grid>
              
              {req.employee_reason && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Your Reason</Typography>
                  <Typography variant="body2">{req.employee_reason}</Typography>
                </Grid>
              )}
            </Grid>

            {/* Appeal Information */}
            {req.appeal_outcome && (
              <Alert 
                severity={req.appeal_outcome === 'upheld' ? 'success' : 'error'} 
                sx={{ mt: 2 }}
              >
                <AlertTitle>Appeal {req.appeal_outcome === 'upheld' ? 'Upheld' : 'Rejected'}</AlertTitle>
                Your appeal was {req.appeal_outcome}.
              </Alert>
            )}
          </Collapse>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button 
            size="small" 
            onClick={() => setExpandedId(isExpanded ? null : req.id)}
            endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
          >
            {isExpanded ? 'Less' : 'More Details'}
          </Button>

          <Box>
            {/* Withdraw (only for pending requests) */}
            {['submitted', 'acknowledged', 'consultation_scheduled'].includes(req.status) && (
              <Button 
                size="small" 
                color="error"
                onClick={() => setWithdrawDialog({ open: true, request: req })}
              >
                Withdraw
              </Button>
            )}

            {/* Appeal (only for refused requests) */}
            {req.status === 'refused' && (
              <Button 
                size="small" 
                variant="outlined"
                startIcon={<Gavel />}
                onClick={() => setAppealDialog({ open: true, request: req })}
              >
                Appeal
              </Button>
            )}
          </Box>
        </CardActions>
      </Card>
    );
  };

  // ============================================================
  // DIALOGS
  // ============================================================

  const AppealDialog = () => {
    const [grounds, setGrounds] = useState('');

    return (
      <Dialog 
        open={appealDialog.open} 
        onClose={() => setAppealDialog({ open: false, request: null })} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Appeal Decision</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            You have the right to appeal if you believe the decision was:
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Based on incorrect facts</li>
              <li>Did not properly consider your request</li>
              <li>Did not follow the correct process</li>
              <li>Used an invalid reason for refusal</li>
            </ul>
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Grounds for Appeal"
            value={grounds}
            onChange={(e) => setGrounds(e.target.value)}
            placeholder="Explain why you are appealing this decision..."
            required
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppealDialog({ open: false, request: null })}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => handleAppeal(grounds)}
            disabled={!grounds.trim()}
          >
            Submit Appeal
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const WithdrawDialog = () => (
    <Dialog 
      open={withdrawDialog.open} 
      onClose={() => setWithdrawDialog({ open: false, request: null })}
    >
      <DialogTitle>Withdraw Request?</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to withdraw this flexible working request?
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          This will count as one of your 2 requests per year.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setWithdrawDialog({ open: false, request: null })}>Cancel</Button>
        <Button variant="contained" color="error" onClick={handleWithdraw}>
          Withdraw
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ============================================================
  // MAIN RENDER
  // ============================================================

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">My Flexible Working Requests</Typography>
        {onNewRequest && (
          <Button variant="contained" onClick={onNewRequest}>
            New Request
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {requests.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Flexible Working Requests
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You haven't made any flexible working requests yet.
            </Typography>
            {onNewRequest && (
              <Button variant="contained" onClick={onNewRequest}>
                Make a Request
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Tabs */}
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label={`All (${requests.length})`} />
            <Tab label={`Pending (${requests.filter(r => ['submitted', 'acknowledged', 'consultation_scheduled', 'consultation_held'].includes(r.status)).length})`} />
            <Tab label={`Approved (${requests.filter(r => ['approved', 'approved_trial'].includes(r.status)).length})`} />
            <Tab label={`Other (${requests.filter(r => ['refused', 'appealed', 'withdrawn'].includes(r.status)).length})`} />
          </Tabs>

          {/* Request Cards */}
          {filteredRequests.length === 0 ? (
            <Alert severity="info">No requests in this category</Alert>
          ) : (
            filteredRequests.map(renderRequestCard)
          )}
        </>
      )}

      {/* Dialogs */}
      <AppealDialog />
      <WithdrawDialog />
    </Box>
  );
};

export default MyFlexibleWorkingRequests;